import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Package, Users, Calendar, FileDown } from 'lucide-react';
import { Sale, Customer, Plant, Payment } from '../../types';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { reportService, customerService, plantService } from '../../services/firebase';
import ExportReportModal from './ExportReportModal';

const Reports: React.FC = () => {
  const [orders, setOrders] = useState<Sale[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [customersData, plantsData] = await Promise.all([
          customerService.getAllCustomers(),
          plantService.getAllPlants()
        ]);
        
        setCustomers(customersData);
        setPlants(plantsData);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateValue: any): Date => {
    if (!dateValue) return new Date();
    
    // If it's a Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a number (timestamp)
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    
    // Try to create a Date from the value
    return new Date(dateValue);
  };

  const [filteredOrders, setFilteredOrders] = useState<Sale[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);

  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        const days = parseInt(dateRange);
        const endDate = new Date();
        const startDate = subDays(endDate, days);
        
        const [ordersData, paymentsData] = await Promise.all([
          reportService.getOrdersByDateRange(startDate, endDate),
          reportService.getPaymentsByDateRange(startDate, endDate)
        ]);
        
        setFilteredOrders(ordersData);
        setFilteredPayments(paymentsData);
      } catch (error) {
        console.error('Error fetching filtered data:', error);
      }
    };

    if (dateRange) {
      fetchFilteredData();
    }
  }, [dateRange]);

  // Remove this line as we're now using state variables

  const calculateStats = () => {
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.totalAmount, 0);
    const totalPaid = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const outstandingAmount = filteredOrders.reduce((sum, order) => sum + order.remainingAmount, 0);
    const totalOrders = filteredOrders.length;
    const totalCustomers = customers.length;
    const totalPlants = plants.length;

    // Top customers by total spent
    const customerStats = customers.map(customer => {
      const customerOrders = orders.filter(order => order.customerId === customer.id);
      const totalSpent = customerOrders.reduce((sum, order) => sum + order.amountPaid, 0);
      return { ...customer, totalSpent };
    }).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 5);

    // Plant popularity
    const plantStats = plants.map(plant => {
      const plantOrders = orders.filter(order => order.customerId === plant.id); // This would need to be adjusted based on actual data structure
      const totalSold = plantOrders.reduce((sum, order) => sum + order.totalAmount, 0);
      return { ...plant, totalSold };
    }).sort((a, b) => b.totalSold - a.totalSold);

    return {
      totalRevenue,
      totalPaid,
      outstandingAmount,
      totalOrders,
      totalCustomers,
      totalPlants,
      customerStats,
      plantStats,
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Reports & Analytics</h1>
            <p className="text-purple-100 text-lg">Business insights and performance metrics</p>
          </div>
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
            <label className="text-sm font-medium text-white">Date Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-white/20 rounded-lg px-3 py-2 text-sm bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 backdrop-blur-sm"
            >
              <option value="7" className="text-gray-900">Last 7 days</option>
              <option value="30" className="text-gray-900">Last 30 days</option>
              <option value="90" className="text-gray-900">Last 90 days</option>
              <option value="365" className="text-gray-900">Last year</option>
            </select>
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
            >
              <FileDown className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-lg font-bold text-green-600">₹</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                  <dd className="text-lg font-medium text-gray-900">₹{stats.totalRevenue.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Orders</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalOrders}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Farmers</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalCustomers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Outstanding</dt>
                  <dd className="text-lg font-medium text-gray-900">₹{stats.outstandingAmount.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Customers</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {stats.customerStats.map((customer, index) => (
                <li key={customer.id} className="py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-medium text-sm">{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{customer.name}</p>
                      <p className="text-sm text-gray-500">{customer.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">₹{customer.totalSpent.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">{customer.totalOrders} orders</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Orders</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {filteredOrders.slice(0, 5).map((order) => (
                <li key={order.id} className="py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Package className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{order.customerName}</p>
                      <p className="text-sm text-gray-500">
                        {format(formatDate(order.orderDate), 'MMM dd, yyyy')} • {order.totalTrays} trays
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">₹{order.totalAmount.toLocaleString()}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        order.status.name === 'Delivered' ? 'bg-green-100 text-green-800' :
                        order.status.name === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status.name}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Payment Summary</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <span className="text-lg font-bold text-green-600">₹</span>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">Total Collected</p>
                  <p className="text-lg font-semibold text-green-900">₹{stats.totalPaid.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-red-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">Outstanding</p>
                  <p className="text-lg font-semibold text-red-900">₹{stats.outstandingAmount.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">Collection Rate</p>
                  <p className="text-lg font-semibold text-blue-900">
                    {stats.totalRevenue > 0 ? Math.round((stats.totalPaid / stats.totalRevenue) * 100) : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Export Modal */}
      <ExportReportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        currentDateRange={dateRange}
        orders={filteredOrders}
        payments={filteredPayments}
        customers={customers}
      />
    </div>
  );
};

export default Reports; 